import { computed, toValue } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * What one finished run says about itself, beside its findings: partial or
 * whole, which parts of EVS 875 it searched, how many checklist items it
 * really looked at, and its cost line.
 *
 * One home for the appraiser's page (`Evaluation/QualityCheck.vue`) and the
 * REdata admin's read-only view of the same run (`Admin/ReportQcCheck.vue`,
 * RED-389), so both say the same thing about the same run. Which notice a page
 * shows WHEN (its view state) stays with the page.
 *
 * @param {import('vue').MaybeRefOrGetter<object|null>} check  the run (`reportQc.check`)
 * @param {import('vue').MaybeRefOrGetter<Array|null>} checklist  its checklist states
 */
export function useQcRunSummary(check, checklist) {
  const { t } = useI18n()

  /**
   * RED-341 D9: a finished run that answered fewer questions than it asked.
   * `checksTotal` / `checksDone` count QUESTIONS; a failed question leaves its
   * checklist items "not checked", which the checklist panel shows per item.
   */
  const isPartial = computed(() => {
    const c = toValue(check)
    return c?.status === 'completed'
      && Number.isInteger(c.checksDone)
      && Number.isInteger(c.checksTotal)
      && c.checksDone < c.checksTotal
  })

  /**
   * RED-344: a run the cost cap stopped says so. "Run it again" is the wrong
   * advice there — the same report would stop at the same place — so the cap
   * has its own sentences. Every other reason keeps RED-341's text.
   */
  const stoppedByCostCap = computed(() => toValue(check)?.stopReason === 'cost_cap')
  const partialBodyKey = computed(() => stoppedByCostCap.value
    ? 'messages.evaluation.qualityCheck.partial.costCapBody'
    : 'messages.evaluation.qualityCheck.partial.body')
  const partialEmptyBodyKey = computed(() => stoppedByCostCap.value
    ? 'messages.evaluation.qualityCheck.partial.costCapEmptyBody'
    : 'messages.evaluation.qualityCheck.partial.emptyBody')

  /**
   * RED-342: the sentence for a run that searched only part of EVS 875, or
   * none of it. Empty when the standard was complete or the run asked no
   * standards question.
   */
  const standardsNotice = computed(() => {
    const c = toValue(check)
    if (c?.status !== 'completed') {
      return ''
    }
    if (c.standardsSkipped) {
      return t('messages.evaluation.qualityCheck.standards.skippedBody')
    }
    const missing = c.standardsMissing ?? []
    return missing.length > 0
      ? t('messages.evaluation.qualityCheck.standards.missingBody', { parts: missing.join(', ') })
      : ''
  })

  /**
   * How many checklist ITEMS the run really looked at: what "Kontrolliti N
   * punkti" must say. Before RED-341 the payload has no per-item state, and
   * the old total stands.
   */
  const checkedItemCount = computed(() => {
    const list = toValue(checklist) ?? []
    if (list.some(item => typeof item.state === 'string')) {
      return list.filter(item => item.state !== 'not_checked').length
    }
    return toValue(check)?.checksTotal ?? 22
  })

  /**
   * RED-373: the run's cost, when the server sent `usd` (REdata admins only;
   * the appraiser never sees money). Empty otherwise.
   */
  const costLine = computed(() => {
    const usd = toValue(check)?.usd
    if (usd === undefined || usd === null) {
      return ''
    }
    const amount = new Intl.NumberFormat('et-EE', { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(Number(usd))
    return t('messages.evaluation.qualityCheck.cost.line', { usd: amount })
  })

  return { isPartial, partialBodyKey, partialEmptyBodyKey, standardsNotice, checkedItemCount, costLine }
}
